import { Heart, Mic2, Crown, MessageCircle } from "lucide-react";
import { Reveal, Eyebrow, SectionShell, GoldCta, IMAGES } from "./shared";

export const VslSection = () => (
  <SectionShell id="testimonianza" className="grain">
    <div className="max-w-4xl mx-auto text-center">
      <Reveal>
        <Eyebrow className="mb-4">La Mia Testimonianza</Eyebrow>
        <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="vsl-title">
          Da uomo in cerca di approvazione a uomo <span className="gold-text">con una missione.</span>
        </h2>
        <p className="text-[#b8b2a7] text-base md:text-lg mt-6 max-w-2xl mx-auto">
          «Dalle insicurezze, dagli eccessi e dalla ricerca del successo, fino all'incontro con Cristo e alla decisione di mettere la mia storia al servizio degli altri.»
        </p>
      </Reveal>
      <Reveal delay={0.12} className="mt-12">
        <div className="relative aspect-video rounded-2xl overflow-hidden border border-[#d4af37]/30 shadow-[0_40px_120px_-40px_rgba(212,175,55,0.25)] bg-black">
          <iframe
            data-testid="vsl-video"
            src="https://www.youtube.com/embed/OQAtJmuj0_g"
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            title="La testimonianza di Elio Briotti"
          />
        </div>
      </Reveal>
      <Reveal delay={0.15} className="mt-10">
        <p className="font-quote italic text-lg sm:text-xl text-[#f3e5ab] max-w-2xl mx-auto">
          «Non ti racconto questa storia perché ho raggiunto la perfezione, ma perché ho compreso che una vita può cambiare quando la fede diventa responsabilità e azione.»
        </p>
        <div className="mt-9">
          <GoldCta testId="vsl-cta">Scrivimi in privato su WhatsApp</GoldCta>
        </div>
      </Reveal>
    </div>
  </SectionShell>
);

export const BeforeAfterSection = () => (
  <SectionShell className="bg-[#0a0908]">
    <Reveal className="text-center">
      <h2 className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl mx-auto" data-testid="beforeafter-title">
        Non è stata soltanto una trasformazione fisica.
      </h2>
      <p className="font-display uppercase text-xl sm:text-2xl gold-text tracking-[0.15em] mt-4">È stata una ricostruzione.</p>
    </Reveal>
    <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto mt-12" data-testid="before-after">
      <Reveal>
        <div className="relative rounded-2xl overflow-hidden border border-white/10">
          <img src={IMAGES.prima} alt="Elio Briotti prima della trasformazione, 108 kg" loading="lazy" className="w-full h-[280px] sm:h-[420px] object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080706]/90 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <p className="font-display text-sm sm:text-base tracking-[0.25em] uppercase text-[#b8b2a7]">Prima</p>
            <p className="text-[11px] sm:text-xs text-[#7a7367] mt-1">108 kg</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/40 shadow-[0_20px_60px_-25px_rgba(212,175,55,0.35)]">
          <img src={IMAGES.dopo} alt="Elio Briotti dopo la trasformazione" loading="lazy" className="w-full h-[280px] sm:h-[420px] object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080706]/90 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-4">
            <p className="font-display text-sm sm:text-base tracking-[0.25em] uppercase gold-text">Dopo</p>
            <p className="text-[11px] sm:text-xs text-[#b8b2a7] mt-1">Una nuova disciplina</p>
          </div>
        </div>
      </Reveal>
    </div>
    <Reveal className="text-center mt-12">
      <p className="text-[#b8b2a7] text-base max-w-2xl mx-auto leading-relaxed">
        «Il cambiamento che vedi nel corpo è soltanto la parte visibile di una trasformazione iniziata molto più in profondità: nella mente, nel cuore, nella fede e nelle decisioni di ogni giorno.»
      </p>
      <p className="font-display text-2xl sm:text-3xl uppercase gold-text tracking-[0.2em] mt-8">Fede in azione.</p>
    </Reveal>
  </SectionShell>
);

const chapters = [
  { n: "01", t: "L'uomo invisibile", d: "«Per anni ho cercato il mio valore nell'approvazione degli altri. Ero insicuro, sensibile e avevo paura del giudizio. Volevo diventare qualcuno, essere rispettato e sentirmi finalmente abbastanza.»" },
  { n: "02", t: "Il personaggio", d: "«Ho iniziato a costruirmi un'immagine. La Polizia Penitenziaria, le feste, gli eccessi, il business online, il denaro. Pensavo che avere successo fuori significasse avere finalmente valore dentro. Ma stavo soltanto diventando sempre più distante da me stesso.»" },
  { n: "03", t: "Il vuoto", d: "«Ho conosciuto le dipendenze, le scelte sbagliate e le perdite economiche. Sono arrivato a pesare 108 chili. Ma il peso più grande non era sul mio corpo. Era nella mente e nel cuore.»" },
  { n: "04", t: "L'incontro con Cristo", d: "«A Malta ho incontrato Cristo. Non una nuova immagine da mostrare agli altri, ma una nuova identità da costruire giorno dopo giorno. Ho iniziato a comprendere che la fede non poteva rimanere soltanto qualcosa che professavo con le parole. Doveva diventare visibile nelle mie scelte.»" },
  { n: "05", t: "La ricostruzione con Chiara", d: "«Insieme a mia moglie Chiara abbiamo attraversato perdite, lutti e prove che ci hanno segnato profondamente. Abbiamo imparato a sostenerci, a ritrovarci e a ricostruire la nostra relazione, mettendo Cristo al centro. Dentro quel viaggio è cresciuta una domanda.»" },
];

export const StoryChapters = () => (
  <SectionShell id="storia">
    <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-start">
      <div className="lg:sticky lg:top-28">
        <Reveal>
          <Eyebrow className="mb-4">La Storia di Elio — Dalle Ferite alla Missione</Eyebrow>
          <h2 className="font-display uppercase text-2xl sm:text-3xl leading-snug" data-testid="story-title">
            Ho dovuto perdere l'uomo che fingevo di essere per scoprire <span className="gold-text">l'uomo che dovevo diventare.</span>
          </h2>
          <div className="relative mt-10 rounded-2xl overflow-hidden border border-[#d4af37]/20">
            <img src={IMAGES.story} alt="Elio Briotti negli anni nella Polizia Penitenziaria" loading="lazy" className="w-full h-[420px] object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080706]/80 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 font-quote italic text-lg text-[#f3e5ab]">Gli anni in divisa — il personaggio</p>
          </div>
        </Reveal>
      </div>

      <div className="relative">
        <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-[#d4af37]/50 via-[#d4af37]/15 to-transparent" />
        <div className="space-y-12">
          {chapters.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.06} className="relative pl-14" y={28}>
              <span className="absolute left-0 top-0 w-8 h-8 rounded-full border border-[#d4af37]/50 bg-[#080706] flex items-center justify-center font-display text-[10px] text-[#d4af37]">
                {step.n}
              </span>
              <h3 className="font-display text-lg tracking-[0.12em] uppercase text-[#f3e5ab]">Capitolo {step.n} — {step.t}</h3>
              <p className="font-quote text-lg md:text-xl text-[#b8b2a7] mt-3 leading-relaxed">{step.d}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="my-14">
          <p className="font-display text-xl sm:text-2xl lg:text-3xl uppercase gold-text leading-snug text-center lg:text-left" data-testid="story-quote">
            «Signore, Dio, come posso mettere ciò che ho vissuto al servizio di qualcuno?»
          </p>
        </Reveal>

        <Reveal className="relative pl-14">
          <span className="absolute left-0 top-0 w-8 h-8 rounded-full border border-[#d4af37]/50 bg-[#080706] flex items-center justify-center font-display text-[10px] text-[#d4af37]">06</span>
          <h3 className="font-display text-lg tracking-[0.12em] uppercase text-[#f3e5ab]">Capitolo 06 — La Missione</h3>
          <p className="font-quote text-lg md:text-xl text-[#b8b2a7] mt-3 leading-relaxed">
            «Nell'aprile del 2026 ho lanciato Fede & Fitness Italia. E sono partito. Non avevo tutte le risposte. Non ero diventato perfetto. Ma avevo trovato una direzione. Aiutare altre persone a trasformare la propria fede in azioni concrete.»
          </p>
        </Reveal>

        <Reveal className="mt-14 pl-14">
          <p className="text-base md:text-lg text-[#fbf9f5] border-l-2 border-[#d4af37] pl-5 py-1 leading-relaxed">
            Da quella stessa missione nasce <span className="font-semibold gold-text">Uomo Biblico</span>. Un percorso dedicato agli uomini che vogliono ricostruire la propria identità, sviluppare disciplina, riconoscere i propri talenti e costruire qualcosa che rimanga.
          </p>
        </Reveal>
      </div>
    </div>
  </SectionShell>
);

const situations = [
  { n: "01", t: "Paura del giudizio", d: "Hai una storia, idee e talenti, ma continui a nasconderti perché temi l'opinione degli altri." },
  { n: "02", t: "Lussuria e autocontrollo", d: "Vuoi imparare a governare i tuoi impulsi e interrompere abitudini che stanno condizionando le tue scelte." },
  { n: "03", t: "Pigrizia e passività", d: "Continui a rimandare e ad aspettare il momento perfetto, invece di assumerti la responsabilità di iniziare." },
  { n: "04", t: "Identità e direzione", d: "Lavori e affronti le tue giornate, ma senti di non avere ancora compreso pienamente i tuoi talenti e il tuo proposito." },
  { n: "05", t: "Leadership e relazioni", d: "Desideri diventare un uomo più affidabile, responsabile e presente nella vita delle persone che ami." },
  { n: "06", t: "Voce e progetto", d: "Vorresti comunicare ciò che hai dentro e sviluppare progetti concreti, ma non sai da dove iniziare." },
];

export const RecognizeSection = () => (
  <SectionShell className="bg-[#0a0908] grain">
    <Reveal className="text-center">
      <Eyebrow className="mb-4">Questo percorso è per te?</Eyebrow>
      <h2 className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-4xl mx-auto" data-testid="recognize-title">
        Credi in Dio. Ma stai davvero mettendo la tua <span className="gold-text">fede in azione?</span>
      </h2>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14" data-testid="recognize-grid">
      {situations.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.06}>
          <div className="ub-card rounded-2xl p-7 h-full relative overflow-hidden">
            <span className="absolute -top-4 -right-1 font-display text-[80px] leading-none text-[#d4af37]/[0.07] select-none">{s.n}</span>
            <p className="eyebrow">{s.n}</p>
            <h3 className="font-display uppercase text-lg text-[#f3e5ab] tracking-wide mt-2">{s.t}</h3>
            <p className="text-sm text-[#b8b2a7] mt-3 leading-relaxed">{s.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal className="text-center mt-16">
      <p className="font-display text-xl sm:text-2xl lg:text-3xl uppercase text-[#fbf9f5]" data-testid="recognize-phrase">
        La fede non è un alibi per <span className="text-[#a62424]">rimanere fermo.</span>
      </p>
      <p className="text-[#b8b2a7] text-base mt-5 max-w-xl mx-auto">
        «Pregare è fondamentale. Ma ci sono scelte, responsabilità e azioni che nessuno può compiere al posto tuo.»
      </p>
      <div className="mt-9">
        <GoldCta testId="recognize-cta">Scrivimi in privato su WhatsApp</GoldCta>
      </div>
    </Reveal>
  </SectionShell>
);

const pillars = [
  {
    icon: Heart, n: "01", name: "Cuore", title: "Ricostruisci l'uomo.",
    quote: "«Prima di costruire qualcosa fuori, devi imparare a governare ciò che accade dentro.»",
    items: ["Identità in Cristo", "Disciplina spirituale e personale", "Paura del giudizio", "Autocontrollo e gestione degli impulsi", "Superamento della passività", "Cura del corpo e delle abitudini", "Responsabilità e relazioni", "Leadership familiare"],
  },
  {
    icon: Mic2, n: "02", name: "Voce", title: "Porta alla luce ciò che Dio ti ha affidato.",
    quote: "«La tua storia non è soltanto qualcosa che hai vissuto. Può diventare uno strumento per comunicare, servire e creare valore.»",
    items: ["Scoperta dei talenti", "Riconoscimento delle competenze", "Chiarezza sulla propria missione", "Comunicazione", "Storytelling personale", "Superamento del blocco nell'esporsi", "Personal branding", "Creazione di contenuti online"],
  },
  {
    icon: Crown, n: "03", name: "Eredità", title: "Costruisci qualcosa che rimanga.",
    quote: "«Non sei chiamato soltanto a consumare il presente, ma a utilizzare con responsabilità ciò che hai ricevuto.»",
    items: ["Visione personale", "Leadership in casa e fuori", "Definizione degli obiettivi", "Sviluppo professionale", "Competenze economiche e progettuali", "Capacità di creare valore", "Responsabilità verso la famiglia", "Costruzione di un'eredità valoriale"],
  },
];

export const MethodSection = () => (
  <SectionShell id="metodo">
    <Reveal className="text-center">
      <Eyebrow className="mb-4">Il Metodo — Cuore • Voce • Eredità</Eyebrow>
      <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="method-title">
        Tre pilastri. <span className="gold-text">Una nuova direzione.</span>
      </h2>
      <p className="text-[#b8b2a7] text-base md:text-lg mt-6 max-w-2xl mx-auto">
        «Un percorso di trasformazione che parte dall'identità, passa attraverso la comunicazione e prende forma in ciò che scegli di costruire.»
      </p>
    </Reveal>
    <div className="space-y-8 mt-14">
      {pillars.map((p, i) => (
        <Reveal key={p.n} delay={i * 0.05}>
          <div className="ub-card rounded-2xl p-8 sm:p-12 relative overflow-hidden" data-testid={`method-pillar-${p.name.toLowerCase()}`}>
            <span className="absolute -top-6 -right-2 font-display text-[120px] leading-none text-[#d4af37]/[0.06] select-none">{p.n}</span>
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-full border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37]">
                <p.icon size={22} strokeWidth={1.5} />
              </span>
              <div>
                <p className="eyebrow">{p.n} — {p.name}</p>
                <h3 className="font-display uppercase text-xl sm:text-2xl lg:text-3xl text-[#fbf9f5] mt-1">{p.title}</h3>
              </div>
            </div>
            <p className="font-quote italic text-lg sm:text-xl text-[#f3e5ab] mt-7">{p.quote}</p>
            <div className="flex flex-wrap gap-2.5 mt-7">
              {p.items.map((it) => (
                <span key={it} className="text-xs px-3 py-1.5 rounded-full bg-[#d4af37]/[0.07] border border-[#d4af37]/15 text-[#b8b2a7]">{it}</span>
              ))}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </SectionShell>
);

const phases = [
  { n: "01", t: "Comprendiamo dove sei", d: "Analizziamo la situazione attuale, le difficoltà, le abitudini e gli obiettivi." },
  { n: "02", t: "Definiamo dove vuoi andare", d: "Costruiamo una direzione attraverso i tre pilastri Cuore, Voce ed Eredità." },
  { n: "03", t: "Passiamo all'azione", d: "Lavoriamo attraverso sessioni individuali, esercizi e responsabilità quotidiane." },
  { n: "04", t: "Consolidiamo il percorso", d: "Monitoriamo i progressi e lavoriamo per costruire continuità e autonomia." },
];

const mentorshipIncludes = ["Confronto diretto con Elio", "Coaching individuale", "Percorso personalizzato", "Obiettivi e azioni concrete", "Strumenti pratici", "Lavoro su identità, disciplina e direzione"];

export const MentorshipSection = () => (
  <SectionShell id="mentorship" className="bg-[#0a0908]">
    <Reveal className="text-center">
      <Eyebrow className="mb-4">Il Percorso 1:1</Eyebrow>
      <h2 className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-4xl mx-auto" data-testid="mentorship-title">
        Non hai bisogno di un altro video motivazionale. <span className="gold-text">Hai bisogno di metterti in azione.</span>
      </h2>
      <p className="text-[#b8b2a7] text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
        «Uomo Biblico è un percorso privato in cui lavoro personalmente con te per aiutarti a trasformare intenzioni, convinzioni e obiettivi in decisioni concrete. Non un programma uguale per tutti, ma un lavoro costruito intorno alla tua situazione.»
      </p>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14" data-testid="mentorship-phases">
      {phases.map((f, i) => (
        <Reveal key={f.n} delay={i * 0.07}>
          <div className="ub-card rounded-2xl p-7 h-full">
            <p className="font-display text-3xl gold-text">{f.n}</p>
            <h3 className="font-display uppercase text-base text-[#f3e5ab] tracking-wide mt-4">{f.t}</h3>
            <p className="text-sm text-[#b8b2a7] mt-3 leading-relaxed">{f.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-10">
      <div className="ub-card rounded-2xl p-8 sm:p-10 !border-[#d4af37]/35" data-testid="mentorship-includes">
        <h3 className="font-display uppercase text-lg gold-text tracking-[0.15em]">Cosa trovi nella mentorship</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 mt-7">
          {mentorshipIncludes.map((it) => (
            <p key={it} className="flex gap-3 text-sm text-[#b8b2a7]">
              <span className="text-[#d4af37] mt-0.5">✦</span> {it}
            </p>
          ))}
        </div>
      </div>
    </Reveal>
    <Reveal className="text-center mt-12">
      <GoldCta testId="mentorship-cta">Scrivimi in privato su WhatsApp</GoldCta>
      <p className="text-xs text-[#7a7367] mt-5">
        «Raccontami la tua situazione e ti spiegherò personalmente come funziona il percorso.»
      </p>
    </Reveal>
  </SectionShell>
);

export const AboutElioSection = () => (
  <SectionShell>
    <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-center">
      <Reveal>
        <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/25">
          <img src={IMAGES.dopo} alt="Elio Briotti, mentor e life coach cristiano" loading="lazy" className="w-full h-[460px] sm:h-[540px] object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080706]/80 via-transparent to-transparent" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Eyebrow className="mb-4">Chi è Elio Briotti</Eyebrow>
        <h2 className="font-display uppercase text-2xl sm:text-3xl leading-snug" data-testid="about-title">
          Non ti parlo da uomo perfetto. Ti parlo da uomo che ha deciso di <span className="gold-text">ricominciare.</span>
        </h2>
        <div className="text-[#b8b2a7] text-base leading-relaxed mt-7 space-y-4">
          <p>«Sono Elio Briotti, fondatore di Fede & Fitness Italia e creatore di Uomo Biblico.</p>
          <p>Ho conosciuto il peso della paura, dell'approvazione, degli eccessi e delle decisioni sbagliate. Ma ho conosciuto anche il potere trasformativo della fede in Cristo, quando smetti di limitarti a dichiararla e inizi a viverla concretamente.</p>
          <p>Oggi metto la mia esperienza al servizio degli uomini che desiderano lavorare sulla propria identità, sulla disciplina, sui propri talenti e sulla direzione della propria vita.</p>
          <p>Non voglio insegnarti a diventare la copia di qualcun altro. Voglio aiutarti a riconoscere ciò che Dio ti ha affidato e a prenderti la responsabilità di farlo fruttare.»</p>
        </div>
        <div className="mt-8">
          <p className="font-display text-lg tracking-[0.25em] uppercase text-[#fbf9f5]">Elio Briotti</p>
          <p className="text-sm text-[#d4af37] mt-1.5">Cuore • Voce • Eredità</p>
        </div>
      </Reveal>
    </div>
  </SectionShell>
);
